import React, { useEffect, useState } from 'react';
import { fastapi } from '../../services/api';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faStore, faMoneyBillTrendUp, faBolt } from '@fortawesome/free-solid-svg-icons';



const AdminDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<{
    total_tenants: number;
    active_bots: number;
    platform_revenue: number;
    avg_response_time: string;
    token_usage_top: { name: string; tokens: number }[];
    revenue_history: { name: string; revenue: number }[];
  } | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fastapi.get('/admin/dashboard/stats');
        setStats(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStats();
  }, []);

  const tokenData = stats?.token_usage_top || [];
  const chartData = stats?.revenue_history || [];
  return (
    <div className="p-8 space-y-8 animate-fade-in-up">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black text-white">
            Platform Overview
          </h1>
          <p className="text-white/50 mt-1">Real-time metrics for Taratic AI ecosystem</p>
        </div>
      </header>

      {/* Top Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="relative group p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl overflow-hidden transition-all hover:bg-white/10 hover:border-pink-500/30 hover:shadow-[0_0_30px_rgba(236,72,153,0.15)]">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform duration-500">
            <FontAwesomeIcon icon={faStore} className="w-24 h-24" />
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-pink-500/20 rounded-2xl flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faStore} className="text-pink-400 text-xl" />
            </div>
            <h3 className="text-lg font-medium text-white/70">Total Tenants</h3>
            <p className="text-4xl font-bold text-white mt-1">{stats?.total_tenants || 0}</p>
          </div>
        </div>

        <div className="relative group p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl overflow-hidden transition-all hover:bg-white/10 hover:border-purple-500/30 hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform duration-500">
            <FontAwesomeIcon icon={faRobot} className="w-24 h-24" />
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-purple-500/20 rounded-2xl flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faRobot} className="text-purple-400 text-xl" />
            </div>
            <h3 className="text-lg font-medium text-white/70">Active AI Bots</h3>
            <p className="text-4xl font-bold text-white mt-1">{stats?.active_bots || 0}</p>
          </div>
        </div>

        <div className="relative group p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl overflow-hidden transition-all hover:bg-white/10 hover:border-green-500/30 hover:shadow-[0_0_30px_rgba(34,197,94,0.15)]">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform duration-500">
            <FontAwesomeIcon icon={faMoneyBillTrendUp} className="w-24 h-24" />
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-green-500/20 rounded-2xl flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faMoneyBillTrendUp} className="text-green-400 text-xl" />
            </div>
            <h3 className="text-lg font-medium text-white/70">Platform Revenue</h3>
            <p className="text-4xl font-bold text-white mt-1">${stats?.platform_revenue?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        <div className="relative group p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl overflow-hidden transition-all hover:bg-white/10 hover:border-yellow-500/30 hover:shadow-[0_0_30px_rgba(234,179,8,0.15)]">
          <div className="absolute top-0 right-0 p-4 opacity-10 transform group-hover:scale-110 transition-transform duration-500">
            <FontAwesomeIcon icon={faBolt} className="w-24 h-24" />
          </div>
          <div className="relative z-10">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-2xl flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faBolt} className="text-yellow-400 text-xl" />
            </div>
            <h3 className="text-lg font-medium text-white/70">Avg. Response Time</h3>
            <p className="text-4xl font-bold text-white mt-1">{stats?.avg_response_time || '0.0s'}</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl">
          <h3 className="text-xl font-semibold text-white/90 mb-6">Revenue Growth</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {chartData.length > 0 ? (
                <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.5}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.5)'}} />
                <YAxis stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.5)'}} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                  <Area type="monotone" dataKey="revenue" stroke="#ec4899" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 italic">No revenue data available</div>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-xl">
          <h3 className="text-xl font-semibold text-white/90 mb-6">Token Usage by Top Comercios</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {tokenData.length > 0 ? (
                <BarChart data={tokenData}>
                <defs>
                  <linearGradient id="colorTokens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0.2}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.5)'}} />
                <YAxis stroke="rgba(255,255,255,0.5)" tick={{fill: 'rgba(255,255,255,0.5)'}} />
                <Tooltip 
                  cursor={{fill: 'rgba(255,255,255,0.05)'}}
                  contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                />
                <Bar dataKey="tokens" fill="url(#colorTokens)" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 italic">No token usage data available</div>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
